/**
 * SegmentRenderer — Renders an array of Segments into inline React elements.
 *
 * Each segment is a piece of inline content. Properties on the segment
 * control wrapping:
 *
 *   text only     → plain text node
 *   + term        → <Term def={...}>
 *   + href        → <a>
 *   + code        → <code>
 *   + strong      → <strong>
 *   + highlight   → adds semantic color class to <code>
 *
 * Properties compose: { text, code, term } → <code><Term>text</Term></code>
 *
 * This component contains ZERO content. All text comes from content.json.
 */

import type { Segment } from '../types';
import Term from './Term';

interface SegmentRendererProps {
  segments: Segment[];
}

/**
 * Maps a highlight value to Tailwind classes for styled code.
 */
const HIGHLIGHT_CLASSES: Record<string, string> = {
  danger: 'px-1.5 py-0.5 rounded bg-rose-500/10 text-rose-400 border border-rose-500/20',
  warning: 'px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/20',
  success: 'px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20',
};

export default function SegmentRenderer({ segments }: SegmentRendererProps) {
  return (
    <>
      {segments.map((seg, i) => {
        // Start with the raw text
        let node: React.ReactNode = seg.text;

        // Layer 1: Term (innermost — wraps the visible text)
        if (seg.term) {
          node = <Term def={seg.term}>{node}</Term>;
        }

        // Layer 2: Strong
        if (seg.strong) {
          node = <strong className="text-slate-300">{node}</strong>;
        }

        // Layer 3: Code
        if (seg.code) {
          const highlightCls = seg.highlight ? HIGHLIGHT_CLASSES[seg.highlight] : '';
          node = (
            <code className={`text-emerald-400 font-mono font-bold ${highlightCls}`}>
              {node}
            </code>
          );
        }

        // Layer 4: Link (outermost — wraps everything)
        if (seg.href) {
          const isExternal = seg.external !== false && seg.href.startsWith('http');
          node = (
            <a
              href={seg.href}
              className="text-emerald-400 hover:underline decoration-emerald-400/30 hover:decoration-emerald-400"
              {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
            >
              {node}
            </a>
          );
        }

        // Wrap in a fragment with key
        return <span key={i}>{node}</span>;
      })}
    </>
  );
}
